import { EXPLORER_PALETTE } from './fractalPalette';
import {
  NEWTON_MODE_CENTERED_QUARTIC,
  NEWTON_MODE_CUBIC,
  NEWTON_MODE_QUARTIC,
  NEWTON_MODE_SEXTIC,
  NEWTON_MODE_SMALE,
} from './fractalIteration';

// Canonical Newton-Raphson relaxation parameter a = 1 + 0i for p(z) = z^3 - 1
export const DEFAULT_NEWTON_C = Object.freeze([1, 0]);
export const DEFAULT_JULIA_C = DEFAULT_NEWTON_C;

// Vertical span is measured in complex-plane units. Derive horizontal span
// from the canvas proportions so both axes have the same units per pixel.
export function viewportFromCenter([x, y], verticalSpan, width, height) {
  const horizontalSpan = verticalSpan * width / height;
  return {
    xRange: [x - horizontalSpan / 2, x + horizontalSpan / 2],
    yRange: [y - verticalSpan / 2, y + verticalSpan / 2]
  };
}

function closeup(center, verticalSpan, height, gScale = 7, phaseOffset = 0.12, newtonC = DEFAULT_NEWTON_C) {
  const width = 1600;
  const frozenNewtonC = Object.freeze([...newtonC]);
  return {
    width,
    height,
    juliaC: frozenNewtonC,
    newtonC: frozenNewtonC,
    ...viewportFromCenter(center, verticalSpan, width, height),
    palette: { ...EXPLORER_PALETTE, gScale, phaseOffset, juliaC: frozenNewtonC }
  };
}

export const closeups = {
  // Six-fold symmetric hub at the origin z = 0 where p'(z) = 3z^2 vanishes.
  originPoleHub: closeup([0, 0], 1.2, 1200, 7, 0.12, [1, 0]),
  // Primary pole preimage z = -2^(-1/3) on the negative real axis where N(z) = 0.
  primaryPoleRosette: closeup([-0.7937005259840998, 0], 0.36, 1200, 6.5, 0.18, [1, 0]),
  // Lakes of Wada braided boundary channel around off-axis pole preimage N(N(z)) = 0.
  wadaBraidedChannel: closeup([-0.561261019831558, 0.1836183489213939], 0.085, 1200, 6, 0.25, [1, 0]),
  // Centered quartic p(z) = z(z^3 - 1) = z^4 - z with 4th-order root at z = 0 and 3 simple poles.
  centeredQuarticHub: closeup([0, 0], 1.8, 1200, 6.5, 0.14, [1, 0, NEWTON_MODE_CENTERED_QUARTIC]),
  // Quartic four-root unity p(z) = z^4 - 1 with triple pole at z = 0 (8-ray compass).
  quarticFourRootCompass: closeup([0, 0], 1.6, 1200, 6.5, 0.16, [1, 0, NEWTON_MODE_QUARTIC]),
  // Sextic six-root unity p(z) = z^6 - 1 with 5th-order pole at z = 0 (12-ray rose window).
  sexticCathedralRose: closeup([0, 0], 1.5, 1200, 6.0, 0.2, [1, 0, NEWTON_MODE_SEXTIC]),
  // Complex-relaxed Newton spiral vortex for a = 1 + 0.32i.
  relaxedSpiralVortex: closeup([-0.793541, -0.124904], 0.42, 1200, 6, 0.35, [1, 0.32]),
  // Under-relaxed damped pinwheel sanctuary for a = 0.78 + 0.42i.
  dampedPinwheelSanctuary: closeup([0, 0], 1.5, 1200, 6.5, 0.42, [0.78, 0.42]),
  // Over-relaxed Celtic knot galaxy for a = 1.24 - 0.28i.
  overrelaxedCelticGalaxy: closeup([0, 0], 1.8, 1200, 7, 0.15, [1.24, -0.28]),
  // Near-boundary over-relaxed cellular filigree for a = 1.56 + 0.32i.
  criticalOverrelaxationFiligree: closeup([-0.7937, 0], 0.65, 1200, 5.5, 0.28, [1.56, 0.32, NEWTON_MODE_CUBIC]),
  // Complex-relaxed quartic mandala p(z) = z^4 - 1 with a = 1.08 + 0.34i.
  quarticSpiralMandala: closeup([0, 0], 1.65, 1200, 6.0, 0.22, [1.08, 0.34, NEWTON_MODE_QUARTIC]),
  // Smale's non-convergent cubic p(z) = z^3 - 2z + 2 with superattracting 2-cycle 0 <-> 1.
  smaleCubicOverview: closeup([0, 0], 2.5, 1200, 7, 0.12, [1, 0, NEWTON_MODE_SMALE]),
  // Twin poles z = +-sqrt(2/3) framing the {0, 1} non-convergent cycle bridge.
  smaleTwinPoleBridge: closeup([0.42, 0], 1.05, 1200, 6.5, 0.2, [1, 0, NEWTON_MODE_SMALE]),
  // Off-axis preimage z = -0.5 + i*sqrt(3)/2 where N_S(z) = 0 creates an archipelago of black trap islands.
  smaleTrapArchipelago: closeup([-0.5, 0.8660254037844386], 0.48, 1200, 6.0, 0.32, [1, 0, NEWTON_MODE_SMALE]),
  // Damped Smale cubic (a = 0.62 + 0.18i) where under-relaxation collapses the 2-cycle trap.
  dampedSmaleRecovery: closeup([0.35, 0], 1.5, 1200, 6.5, 0.38, [0.62, 0.18, NEWTON_MODE_SMALE]),
};

export const polynomialFamilyPresets = [
  {
    section: 'origin-sixfold-pole-hub',
    headingId: 'origin-sixfold-pole-hub-heading',
    title: 'Origin Six-Fold Pole Hub',
    description: 'At the origin z = 0 where p\u2032(z) = 3z\u00b2 vanishes, the Newton map has a double pole that wraps the three cube-root basins twice around the complex plane into a six-rayed starburst.',
    preset: closeups.originPoleHub,
    source: 'https://en.wikipedia.org/wiki/Newton_fractal',
    sourceLabel: 'Wikipedia \u2014 Newton fractal',
  },
  {
    section: 'primary-pole-rosette',
    headingId: 'primary-pole-rosette-heading',
    title: 'Primary Pole Preimage Rosette',
    description: 'Centred on the real pole preimage z = \u22122\u207b\u00b9/\u00b3 \u2248 \u22120.79370053 where N(z) = 0, six braided petal lobes unfold between the upper and lower cube-root basins.',
    preset: closeups.primaryPoleRosette,
    source: 'https://www.3blue1brown.com/lessons/newton-fractal',
    sourceLabel: '3Blue1Brown \u2014 Newton\u2019s Fractal (which Newton knew nothing about)',
  },
  {
    section: 'wada-braided-boundary-channel',
    headingId: 'wada-braided-boundary-channel-heading',
    title: 'Lakes of Wada Braided Channel',
    description: 'By Cayley\u2019s problem and the Lakes of Wada property, every boundary point between two basins of attraction touches all three basins simultaneously, braiding the third root\u2019s basin along every seam.',
    preset: closeups.wadaBraidedChannel,
    source: 'https://en.wikipedia.org/wiki/Lakes_of_Wada',
    sourceLabel: 'Wikipedia \u2014 Lakes of Wada',
  },
  {
    section: 'centered-quartic-hub',
    headingId: 'centered-quartic-hub-heading',
    title: 'Centered Quartic Four-Root Hub',
    description: 'Multiplying z\u00b3 \u2212 1 by z produces p(z) = z\u2074 \u2212 z, turning the origin z = 0 from a pole into a fourth attracting root with fourth-order convergence (N(z) \u2248 \u22123z\u2074) while splitting the old pole into three simple poles at z = 4\u207b\u00b9/\u00b3e\u00b2\u03c0ik/3.',
    preset: closeups.centeredQuarticHub,
  },
  {
    section: 'quartic-four-root-compass',
    headingId: 'quartic-four-root-compass-heading',
    title: 'Quartic Eight-Ray Compass',
    description: 'For the fourth roots of unity p(z) = z\u2074 \u2212 1 at \u00b11 and \u00b1i, the derivative p\u2032(z) = 4z\u00b3 has a triple zero at the origin, creating a third-order pole that winds four distinct root basins into an eight-rayed compass.',
    preset: closeups.quarticFourRootCompass,
  },
  {
    section: 'sextic-cathedral-rose-window',
    headingId: 'sextic-cathedral-rose-window-heading',
    title: 'Sextic Twelve-Ray Rose Window',
    description: 'Raising the degree to p(z) = z\u2076 \u2212 1 places six roots around the unit circle and a fifth-order pole at z = 0 where p\u2032(z) = 6z\u2075 vanishes, weaving twelve radial spokes of nested hexagonal rosettes.',
    preset: closeups.sexticCathedralRose,
  },
];

export const complexRelaxationPresets = [
  {
    section: 'relaxed-spiral-vortex',
    headingId: 'relaxed-spiral-vortex-heading',
    title: 'Complex-Relaxed Spiral Vortex',
    description: 'Introducing a complex relaxation step a = 1 + 0.32i gives each cube root a rotational multiplier 1 \u2212 a = \u22120.32i, twisting the radial Wada necklaces into logarithmic spiral arms.',
    preset: closeups.relaxedSpiralVortex,
    source: 'https://paulbourke.net/fractals/newtonraphson/',
    sourceLabel: 'Paul Bourke \u2014 Newton-Raphson Fractals',
  },
  {
    section: 'damped-pinwheel-sanctuary',
    headingId: 'damped-pinwheel-sanctuary-heading',
    title: 'Damped Pinwheel Sanctuary',
    description: 'Under-relaxing the iteration with a = 0.78 + 0.42i winds the six origin rays into a counter-rotating pinwheel of persimmon, celadon jade, and mineral indigo petals.',
    preset: closeups.dampedPinwheelSanctuary,
  },
  {
    section: 'overrelaxed-celtic-galaxy',
    headingId: 'overrelaxed-celtic-galaxy-heading',
    title: 'Over-Relaxed Celtic Galaxy',
    description: 'With complex over-relaxation a = 1.24 \u2212 0.28i, each step overshoots along a curved tangent and weaves the three basins of attraction into an interlocking Celtic knot around the origin.',
    preset: closeups.overrelaxedCelticGalaxy,
  },
  {
    section: 'critical-overrelaxation-filigree',
    headingId: 'critical-overrelaxation-filigree-heading',
    title: 'Near-Boundary Over-Relaxed Filigree',
    description: 'Pushing the step factor to a = 1.56 + 0.32i brings the root multiplier |1 \u2212 a| \u2248 0.645 closer to the unit-circle stability threshold |1 \u2212 a| = 1, inflating the Julia boundary into a dense cellular filigree of coiled spiral eddies.',
    preset: closeups.criticalOverrelaxationFiligree,
  },
  {
    section: 'quartic-spiral-mandala',
    headingId: 'quartic-spiral-mandala-heading',
    title: 'Relaxed Quartic Spiral Mandala',
    description: 'Combining the four-root quartic p(z) = z\u2074 \u2212 1 with complex relaxation a = 1.08 + 0.34i twists the eight-rayed triple pole at the origin into a four-color spiral mandala.',
    preset: closeups.quarticSpiralMandala,
  },
];

export const smaleBasinPresets = [
  {
    section: 'smale-cubic-overview',
    headingId: 'smale-cubic-overview-heading',
    title: 'Smale\u2019s Non-Convergent Cubic',
    description: 'For p(z) = z\u00b3 \u2212 2z + 2, the inflection point z0 = 0 maps to N(0) = 1 and back to N(1) = 0 in a superattracting period-2 cycle. Starting guesses inside the ink-black basins around 0, 1, and their preimages oscillate forever instead of converging to any root.',
    preset: closeups.smaleCubicOverview,
    source: 'https://en.wikipedia.org/wiki/Newton%27s_method#Failure_of_the_method_to_converge_to_the_root',
    sourceLabel: 'Wikipedia \u2014 Non-convergent Newton basins (z\u00b3 \u2212 2z + 2)',
  },
  {
    section: 'smale-twin-pole-bridge',
    headingId: 'smale-twin-pole-bridge-heading',
    title: 'Twin-Pole Critical Bridge',
    description: 'Between the two simple poles at z = \u00b1\u221a(2/3) \u2248 \u00b10.8165 where p\u2032(z) = 3z\u00b2 \u2212 2 = 0, the black non-convergent lakes surrounding z = 0 and z = 1 are framed by concentric Wada necklaces from the real root r0 \u2248 \u22121.7693 and complex roots r1, r2 \u2248 0.8846 \u00b1 0.5897i.',
    preset: closeups.smaleTwinPoleBridge,
  },
  {
    section: 'smale-trap-archipelago',
    headingId: 'smale-trap-archipelago-heading',
    title: 'Non-Convergent Preimage Archipelago',
    description: 'Because the numerator of N(z) = (2z\u00b3 \u2212 2)/(3z\u00b2 \u2212 2) vanishes at the cube roots of unity, z = \u22121/2 + i\u221a3/2 maps in one step to the critical point z = 0, replicating the ink-black non-convergent basin inside an archipelago of three-color Wada channels.',
    preset: closeups.smaleTrapArchipelago,
  },
  {
    section: 'damped-smale-recovery',
    headingId: 'damped-smale-recovery-heading',
    title: 'Damped Smale Cycle Collapse',
    description: 'Under-relaxing Smale\u2019s cubic with a = 0.62 + 0.18i shifts N_a(0) = a off the period-2 trap. Once the free critical point escapes the cycle and converges to a root, the black non-convergent islands vanish and dissolve into spiraling three-root channels.',
    preset: closeups.dampedSmaleRecovery,
  },
];

export const allSubstructureSections = [
  ...polynomialFamilyPresets,
  ...complexRelaxationPresets,
  ...smaleBasinPresets,
];

export const substructureSections = allSubstructureSections;
